import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Briefcase, Building, MapPin, FileText, DollarSign, ListChecks, UserPlus, Calendar, Star, Loader2 } from "lucide-react";

const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const JobPreview = ({ values }) => (
  <div className="hidden lg:block lg:col-span-1 bg-gray-50 rounded-lg shadow-md p-6 ml-8 sticky top-8">
    <h3 className="text-xl font-bold mb-4 flex items-center text-green-800">
      <Briefcase className="w-6 h-6 mr-2 text-orange-500" /> Job Preview
    </h3>
    <div className="space-y-2">
      <div className="flex items-center text-lg font-semibold text-gray-900">
        <span>{values.title || <span className="italic text-gray-400">Job Title</span>}</span>
      </div>
      <div className="flex items-center text-gray-600">
        <Building className="w-4 h-4 mr-1 text-orange-500" />
        {values.company || <span className="italic text-gray-400">Company</span>}
      </div>
      <div className="flex items-center text-gray-600">
        <MapPin className="w-4 h-4 mr-1 text-orange-500" />
        {values.location || <span className="italic text-gray-400">Location</span>}
      </div>
      <div className="flex items-center text-gray-600">
        <Briefcase className="w-4 h-4 mr-1 text-orange-500" />
        {values.jobType || <span className="italic text-gray-400">Job Type</span>}
      </div>
      <div className="flex items-center text-gray-600">
        <DollarSign className="w-4 h-4 mr-1 text-orange-500" />
        {values.salary || values.salaryRange || <span className="italic text-gray-400">Salary</span>}
      </div>
      <div className="flex items-center text-gray-600">
        <Calendar className="w-4 h-4 mr-1 text-orange-500" />
        {values.deadline || <span className="italic text-gray-400">Deadline</span>}
      </div>
      <div className="mt-4">
        <h4 className="font-semibold mb-1 flex items-center"><FileText className="w-4 h-4 mr-1 text-orange-500" /> Description</h4>
        <p className="text-gray-700 text-sm whitespace-pre-line min-h-[48px]">{values.description || <span className="italic text-gray-400">Job description...</span>}</p>
      </div>
      <div className="mt-2">
        <h4 className="font-semibold mb-1 flex items-center"><ListChecks className="w-4 h-4 mr-1 text-orange-500" /> Requirements</h4>
        <ul className="list-disc list-inside text-gray-700 text-sm min-h-[32px]">
          {(values.requirements ? values.requirements.split(",").map(r => r.trim()).filter(Boolean) : []).map((req, i) => (
            <li key={i}>{req}</li>
          ))}
          {(!values.requirements || !values.requirements.split(",").filter(Boolean).length) && <li className="italic text-gray-400">None listed</li>}
        </ul>
      </div>
      <div className="mt-2">
        <h4 className="font-semibold mb-1 flex items-center"><Star className="w-4 h-4 mr-1 text-orange-500" /> Skills</h4>
        <ul className="list-disc list-inside text-gray-700 text-sm min-h-[32px]">
          {(values.requiredSkills ? values.requiredSkills.split(",").map(s => s.trim()).filter(Boolean) : []).map((skill, i) => (
            <li key={i}>{skill}</li>
          ))}
          {(!values.requiredSkills || !values.requiredSkills.split(",").filter(Boolean).length) && <li className="italic text-gray-400">None listed</li>}
        </ul>
      </div>
    </div>
  </div>
);

const PostJob = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState(null);

  const watchedValues = watch();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);
    setConfirmationMessage(null);

    try {
      const jobData = {
        title: data.title,
        company: data.company,
        location: data.location,
        description: data.description,
        requirements: data.requirements
          ? data.requirements.split(",").map((item) => item.trim())
          : [],
        salary: data.salary,
        salaryRange: data.salaryRange,
        jobType: data.jobType,
        deadline: data.deadline,
        requiredSkills: data.requiredSkills
          ? data.requiredSkills.split(",").map((item) => item.trim())
          : [],
      };

      const response = await fetch(`${API_URL}/jobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(jobData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${JSON.stringify(
            errorData
          )}`
        );
      }

      setConfirmationMessage(
        "Job posted successfully! It will be reviewed by an admin before being published."
      );
      reset();
    } catch (error) {
      setError("Failed to post job. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-md grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-green-800">
          <UserPlus className="w-8 h-8 mr-3 text-orange-500" /> Post a New Job
        </h2>

        {confirmationMessage && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-800 px-4 py-3 rounded mb-6 flex items-center">
            <span className="font-bold mr-2">Success:</span> {confirmationMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-800 px-4 py-3 rounded mb-6 flex items-center">
            <span className="font-bold mr-2">Error:</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Job Details Section */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center text-green-700">
              <Briefcase className="w-5 h-5 mr-2 text-orange-500" /> Job Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block mb-1 font-medium">Job Title</label>
                <input
                  type="text"
                  id="title"
                  placeholder="e.g. Software Engineer"
                  {...register("title", { required: "Job title is required" })}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="company" className="block mb-1 font-medium">Company</label>
                <input
                  type="text"
                  id="company"
                  placeholder="e.g. Acme Corp"
                  {...register("company", { required: "Company name is required" })}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {errors.company && (
                  <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="location" className="block mb-1 font-medium">Location</label>
                <input
                  type="text"
                  id="location"
                  placeholder="e.g. New York, NY or Remote"
                  {...register("location", { required: "Location is required" })}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1">{errors.location.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="jobType" className="block mb-1 font-medium">Job Type</label>
                <select
                  id="jobType"
                  {...register("jobType", { required: "Job type is required" })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select job type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Internship">Internship</option>
                </select>
                {errors.jobType && (
                  <p className="text-red-500 text-sm mt-1">{errors.jobType.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center text-green-700">
              <FileText className="w-5 h-5 mr-2 text-orange-500" /> Job Description
            </h3>
            <textarea
              id="description"
              placeholder="Describe the job responsibilities, expectations, etc."
              {...register("description", { required: "Job description is required" })}
              className="w-full px-3 py-2 border rounded-md"
              rows="4"
            ></textarea>
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
            )}
          </div>

          {/* Requirements & Skills Section */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center text-green-700">
              <ListChecks className="w-5 h-5 mr-2 text-orange-500" /> Requirements & Skills
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="requirements" className="block mb-1 font-medium">Requirements <span className="text-gray-400">(comma-separated)</span></label>
                <input
                  type="text"
                  id="requirements"
                  placeholder="e.g. 3+ years experience, React, Node.js"
                  {...register("requirements")}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label htmlFor="requiredSkills" className="block mb-1 font-medium">Required Skills <span className="text-gray-400">(comma-separated)</span></label>
                <input
                  type="text"
                  id="requiredSkills"
                  placeholder="e.g. JavaScript, Communication, Leadership"
                  {...register("requiredSkills")}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Compensation & Dates Section */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center text-green-700">
              <DollarSign className="w-5 h-5 mr-2 text-orange-500" /> Compensation & Dates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="salary" className="block mb-1 font-medium">Salary</label>
                <input
                  type="text"
                  id="salary"
                  placeholder="e.g. $80,000"
                  {...register("salary")}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label htmlFor="salaryRange" className="block mb-1 font-medium">Salary Range</label>
                <input
                  type="text"
                  id="salaryRange"
                  placeholder="e.g. $70,000 - $90,000"
                  {...register("salaryRange")}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label htmlFor="deadline" className="block mb-1 font-medium">Application Deadline</label>
                <input
                  type="date"
                  id="deadline"
                  {...register("deadline")}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-orange-500 text-white py-3 px-4 rounded-md hover:bg-orange-600 transition duration-300 text-lg font-semibold flex items-center justify-center disabled:opacity-60"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 className="animate-spin w-5 h-5 mr-2" /> : null}
            {isSubmitting ? "Posting..." : "Post Job"}
          </button>
        </form>
      </div>
      <JobPreview values={watchedValues} />
    </div>
  );
};

export default PostJob;
